<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-500/10 mb-4">
        <EnvelopeIcon class="h-6 w-6 text-blue-400" />
      </div>
      <h2 class="text-3xl font-display font-bold text-white">{{ $t('auth.verify_email') }}</h2>
      <p class="mt-2 text-sm text-gray-400">
        {{ $t('auth.verify_email_sent') }} 
        <span class="font-medium text-white">{{ email }}</span>
      </p>
    </div>

    <!-- Actions -->
    <div class="space-y-4">
      <button
        @click="resendVerification"
        :disabled="isLoading || countdown > 0"
        class="w-full btn btn-lg btn-primary bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 border-none"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading || countdown > 0 }"
      >
        <div v-if="isLoading" class="flex items-center">
          <div class="spinner w-5 h-5 mr-2 border-white"></div>
          {{ $t('common.messages.loading') }}
        </div>
        <span v-else>
          {{ countdown > 0 ? `${$t('auth.resend_in')} ${countdown}s` : $t('auth.resend_email') }}
        </span>
      </button>

      <button
        @click="logout"
        class="w-full btn btn-lg btn-secondary bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
      >
        {{ $t('auth.sign_out') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { EnvelopeIcon } from '@heroicons/vue/24/outline'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const email = ref(authStore.user?.email || '')
const isLoading = ref(false)
const countdown = ref(0)
let timer = null

const startTimer = () => {
  countdown.value = 60
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

const resendVerification = async () => {
  if (countdown.value > 0) return
  
  isLoading.value = true
  try {
    await authStore.resendVerification()
    toast.success(t('auth.verification_sent'))
    startTimer()
  } catch (error) {
    toast.error(t('common.messages.error'))
  } finally {
    isLoading.value = false
  }
}

const logout = async () => {
  await authStore.logout()
  router.push({ name: 'login' })
}

onMounted(() => {
  if (!authStore.user) {
    router.push({ name: 'login' })
  }
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>